import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WORKFLOW_STAGE_ORDER = [
  'Pending Team Assignment',
  'Calculated',
  'Pending Approval',
  'Approved',
  'Ready for Payment',
  'Paid',
];

const STAGE_ICONS = {
  'Pending Team Assignment': 'UserPlus',
  'Calculated': 'Calculator',
  'Pending Approval': 'ClipboardCheck',
  'Approved': 'CheckCircle',
  'Ready for Payment': 'CreditCard',
  'Paid': 'Banknote',
};

const STAGE_COLORS = {
  'Pending Team Assignment': 'text-warning border-warning/40 bg-warning/10',
  'Calculated': 'text-blue-400 border-blue-500/40 bg-blue-500/10',
  'Pending Approval': 'text-orange-400 border-orange-500/40 bg-orange-500/10',
  'Approved': 'text-success border-success/40 bg-success/10',
  'Ready for Payment': 'text-purple-400 border-purple-500/40 bg-purple-500/10',
  'Paid': 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10',
};

/**
 * RollbackWorkflowDialog
 *
 * Props:
 *   isOpen         {boolean}
 *   onClose        {() => void}
 *   onConfirm      {(targetStage: string, reason: string) => Promise<void>}
 *   currentStatus  {string}  - The ticket's current status
 *   ticketNumber   {string}
 */
const RollbackWorkflowDialog = ({ isOpen, onClose, onConfirm, currentStatus, ticketNumber }) => {
  const [targetStage, setTargetStage] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const currentIdx = WORKFLOW_STAGE_ORDER?.indexOf(currentStatus);
  // Only stages that come BEFORE the current stage are valid rollback targets
  const availableStages = WORKFLOW_STAGE_ORDER?.slice(0, currentIdx);

  const handleConfirm = async () => {
    if (!targetStage) {
      setError('Please select a stage to roll back to.');
      return;
    }
    if (!reason?.trim()) {
      setError('A rollback reason is mandatory.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onConfirm(targetStage, reason?.trim());
      // Reset on success
      setTargetStage('');
      setReason('');
      onClose();
    } catch (err) {
      setError(err?.message || 'Rollback failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setTargetStage('');
    setReason('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-orange-500 to-red-500">
              <Icon name="RotateCcw" size={18} color="white" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Rollback Workflow</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Ticket <span className="font-mono text-primary">{ticketNumber}</span> — currently <span className="font-medium">{currentStatus}</span>
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
          >
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Warning banner */}
          <div className="flex items-start gap-3 p-3.5 bg-warning/10 border border-warning/30 rounded-xl text-warning text-sm">
            <Icon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
            <span>
              Rolling back will <strong>unlock the ticket</strong>, clear forward-stage metadata, and <strong>recalculate incentives</strong> if needed. Responsible users will be notified.
            </span>
          </div>

          {/* Stage selector */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-2">
              Roll Back To <span className="text-error">*</span>
            </label>
            {availableStages?.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No previous stages available for rollback.</p>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {availableStages?.map((stage) => {
                  const isSelected = targetStage === stage;
                  const colorClass = STAGE_COLORS?.[stage] || 'text-foreground border-border bg-muted/20';
                  return (
                    <button
                      key={stage}
                      type="button"
                      onClick={() => setTargetStage(stage)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium transition-all text-left
                        ${isSelected
                          ? `${colorClass} ring-2 ring-offset-1 ring-offset-card ring-current`
                          : 'border-border bg-background text-foreground hover:border-primary/40 hover:bg-muted/30'
                        }`}
                    >
                      <div className={`p-1.5 rounded-lg ${isSelected ? 'bg-current/10' : 'bg-muted'}`}>
                        <Icon
                          name={STAGE_ICONS?.[stage] || 'Circle'}
                          size={14}
                          className={isSelected ? 'text-current' : 'text-muted-foreground'}
                        />
                      </div>
                      <span>{stage}</span>
                      {isSelected && (
                        <Icon name="CheckCircle" size={16} className="ml-auto text-current" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Reason textarea */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1.5">
              Reason for Rollback <span className="text-error">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e?.target?.value)}
              placeholder="Describe why this ticket is being rolled back (e.g., incorrect calculation discovered after approval)..."
              rows={4}
              maxLength={1000}
              className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none transition-colors"
            />
            <div className="flex justify-between mt-1">
              <span className="text-xs text-muted-foreground">This reason will be saved in the Audit Log.</span>
              <span className="text-xs text-muted-foreground">{reason?.length}/1000</span>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-error/10 border border-error/30 rounded-xl text-error text-sm">
              <Icon name="AlertCircle" size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-border">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            iconName="RotateCcw"
            iconPosition="left"
            onClick={handleConfirm}
            disabled={loading || !targetStage || !reason?.trim()}
          >
            {loading ? 'Rolling Back...' : 'Confirm Rollback'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RollbackWorkflowDialog;
